/**
 * Contact page (sections/contact-form.liquid) progressive enhancements on
 * top of the native Shopify {% form 'contact' %} POST-and-reload flow:
 *  - a client-generated reference, sent as a real `contact[Reference]`
 *    field so it lands in the actual support email, then redisplayed after
 *    reload via localStorage since Shopify's `form` drop doesn't echo
 *    custom fields back on success.
 *  - a loading state during the moment of submission.
 *  - an offline guard: skips a doomed submission when the browser is
 *    confirmed offline and shows the styled "didn't send" message instead
 *    of letting the browser fail natively. True mid-flight failures after
 *    a successful submit can't be caught without converting to a fetch-
 *    based double-request flow, which risks emailing the merchant twice -
 *    out of scope here.
 *  - an "outside support hours" banner computed from the configured hours,
 *    read entirely client-side so it reflects the shopper's own clock.
 */
function initContactForm() {
  const form = document.querySelector('[data-contact-form], .contact-form-section__form');
  if (!form) return;

  const referenceField = form.querySelector('[data-contact-reference]');
  const submitButton = form.querySelector('[data-contact-submit]');
  const offlineBanner = document.querySelector('[data-contact-offline]');

  form.addEventListener('submit', (event) => {
    if (!navigator.onLine) {
      event.preventDefault();
      if (offlineBanner) offlineBanner.hidden = false;
      return;
    }

    if (offlineBanner) offlineBanner.hidden = true;

    const reference = `MSG-${Date.now().toString(36).toUpperCase()}`;
    if (referenceField) referenceField.value = reference;
    window.localStorage.setItem('vellum:lastContactReference', reference);

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('is-loading');
    }
  });

  const successPanel = document.querySelector('[data-contact-success]');
  if (successPanel) {
    const reference = window.localStorage.getItem('vellum:lastContactReference');
    const referenceTarget = successPanel.querySelector('[data-contact-reference-display]');
    if (reference && referenceTarget) {
      referenceTarget.textContent = reference;
      successPanel.querySelector('[data-contact-reference-line]')?.removeAttribute('hidden');
    }
    window.localStorage.removeItem('vellum:lastContactReference');
    successPanel.focus();
  }

  const photoInput = form.querySelector('[data-contact-photo-input]');
  const photoName = form.querySelector('[data-contact-photo-name]');
  photoInput?.addEventListener('change', () => {
    if (photoInput.files?.[0] && photoName) {
      photoName.textContent = photoInput.files[0].name;
    }
  });
}

function isWithinSupportHours(days, startHour, endHour, offsetHours) {
  const shifted = new Date(Date.now() + offsetHours * 3600000);
  const day = shifted.getUTCDay();
  const hour = shifted.getUTCHours() + shifted.getUTCMinutes() / 60;
  return days.includes(day) && hour >= startHour && hour < endHour;
}

function initSupportHoursBanner() {
  const el = document.querySelector('[data-support-hours]');
  if (!el) return;

  const days = (el.dataset.hoursDays || '')
    .split(',')
    .map((value) => parseInt(value, 10))
    .filter((value) => !Number.isNaN(value));
  const startHour = parseFloat(el.dataset.hoursStart);
  const endHour = parseFloat(el.dataset.hoursEnd);
  const offsetHours = parseFloat(el.dataset.hoursOffset);
  if (!days.length || Number.isNaN(startHour) || Number.isNaN(endHour) || Number.isNaN(offsetHours)) return;

  el.hidden = isWithinSupportHours(days, startHour, endHour, offsetHours);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initContactForm();
    initSupportHoursBanner();
  });
} else {
  initContactForm();
  initSupportHoursBanner();
}
